import { type ComponentPropsWithRef, type ElementType, useMemo } from "react"
import Link from "next/link"
import cn from "classnames"
import type { DOMNode, HTMLReactParserOptions } from "html-react-parser"
import parse, { domToReact, Element } from "html-react-parser"

import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import type { ObserverId } from "~/hooks/useSharedInView"

/**
  Inline formatting tags preserved in `inline` mode... everything else
  (h1-h6, p, div, ul, ol, li, blockquote, figure, etc) is treated as a block
  wrapper and flattened to its children, so the output is always safe to nest
  inside a heading element. `a` is handled separately (rendered as a Link).
 */
const INLINE_TAGS = new Set([
  "strong",
  "em",
  "b",
  "i",
  "u",
  "s",
  "sup",
  "sub",
  "code",
  "mark",
  "span",
  "br",
  "abbr",
  "small",
])

type BaseProps<TElement extends ElementType> = {
  animate?: boolean
  inline?: boolean //Inline-injection mode to recursively strip block-level wrappers.. Use when this renderer is nested inside a heading element (e.g. inside `<AnimateText element="h2">`) to preven nested tags like h1
  sharedInViewId?: ObserverId
  delay?: number
  html: string
} & ComponentPropsWithRef<TElement>

// version when className is NOT present
type WithoutClassName<TElement extends ElementType> = {
  element?: TElement
  className?: undefined
} & BaseProps<TElement>

// version when className IS present
type WithClassName<TElement extends ElementType> = {
  element: TElement
  className: string
} & BaseProps<TElement>

type Props<TElement extends ElementType = "div"> =
  | WithoutClassName<TElement>
  | WithClassName<TElement>

export const HtmlRenderer = ({
  animate = false,
  inline = false,
  sharedInViewId = "fadeIn25",
  delay = 0,
  element,
  html,
  ...rest
}: Props<ElementType>) => {
  const Container = element || "div"

  const parsedHtml = useMemo(() => {
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode instanceof Element) {
          const children = domNode.children as DOMNode[]

          if (inline) {
            // Inline-injection mode: keep inline formatting, flatten block
            // wrappers recursively (pass `options` so nested blocks flatten too).
            if (domNode.name === "a") {
              return (
                <Link
                  href={domNode.attribs.href ?? "#"}
                  target={domNode.attribs.target}
                >
                  {domToReact(children, options)}
                </Link>
              )
            }
            if (INLINE_TAGS.has(domNode.name)) return undefined
            return <>{domToReact(children, options)}</>
          }

          switch (domNode.name) {
            case "a":
              return (
                <Link
                  href={domNode.attribs.href ?? "#"}
                  target={domNode.attribs.target}
                >
                  {domToReact(domNode.children as DOMNode[])}
                </Link>
              )
            case "hr":
              return (
                <hr
                  key={"startIndex" in domNode ? domNode.startIndex : undefined}
                />
              )
            default:
              return animate ? (
                <FadeIn
                  sharedInViewId={sharedInViewId}
                  delay={delay}
                  element={domNode.name}
                >
                  {domToReact(children)}
                </FadeIn>
              ) : (
                domToReact(children)
              )
          }
        }
      },
    }

    return parse(html, options)
  }, [animate, delay, html, sharedInViewId, inline])

  return element || rest.className ? (
    <Container {...rest} className={cn(rest.className)}>
      {parsedHtml}
    </Container>
  ) : (
    <>{parsedHtml}</>
  )
}
